import React, { useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { NoRecordsCard } from './_SmartFieldRenderer';

// Indian-standard money formatting (mandatory — ui-ux skill).
const formatINR = (amount) => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n)) return '—';
    return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatNum = (n) => {
    const v = parseFloat(n);
    return Number.isFinite(v) ? v.toLocaleString('en-IN') : '—';
};

const formatCompact = (amount) => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n)) return '—';
    const abs = Math.abs(n);
    if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
    if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
    if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
    return formatINR(n);
};

const titleCase = (s) =>
    typeof s === 'string' && s
        ? s.replace(/\b\w/g, (c) => c.toUpperCase())
        : '—';

// Month ordering for the Indian financial year, so the timeline reads Apr→Mar
// rather than alphabetically. The vendor returns months as lowercase names.
const MONTH_INDEX = {
    april: 0, may: 1, june: 2, july: 3, august: 4, september: 5,
    october: 6, november: 7, december: 8, january: 9, february: 10, march: 11,
};

// GSTR-3B row labels, matched by prefix against `name_of_supplies` / `details`.
const OUTWARD_TAXABLE = '(a) Outward taxable supplies';
const NET_ITC = 'C Net ITC available';

const sumAmounts = (amounts = {}) =>
    ['central_tax', 'state_ut_tax', 'integrated_tax', 'cess']
        .reduce((a, k) => a + (parseFloat(amounts[k]) || 0), 0);

const StatTile = ({ icon, label, value, sub, severity }) => (
    <div className="col-12 md:col-6 lg:col-3">
        <div className="card mb-0 h-full">
            <div className="flex justify-content-between align-items-start">
                <div className="flex flex-column gap-2 min-w-0">
                    <span className="text-600 text-sm font-medium">{label}</span>
                    <span className={`text-xl font-semibold ${severity === 'danger' ? 'text-red-500' : severity === 'success' ? 'text-green-600' : 'text-900'}`}>
                        {value}
                    </span>
                </div>
                <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2 flex-shrink-0">
                    <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true" />
                </div>
            </div>
            {sub && <span className="text-500 text-xs block mt-2">{sub}</span>}
        </div>
    </div>
);

const InfoItem = ({ label, value, money, mono }) => (
    <div className="col-12 md:col-6 lg:col-3">
        <div className="surface-100 border-round p-3 h-full">
            <div className="text-600 text-xs font-medium mb-1">{label}</div>
            <div className={`text-900 text-base font-semibold ${mono ? 'font-mono' : ''}`} style={{ wordBreak: 'break-word' }}>
                {money ? formatINR(value) : (value ?? '—')}
            </div>
        </div>
    </div>
);

const chartOptions = () => ({
    maintainAspectRatio: false,
    plugins: {
        legend: { labels: { color: 'var(--text-color-secondary)', usePointStyle: true, boxWidth: 8 } },
        tooltip: {
            callbacks: { label: (ctx) => `${ctx.dataset.label || ''}: ${formatINR(ctx.parsed.y ?? ctx.parsed)}` },
        },
    },
    scales: {
        x: { ticks: { color: 'var(--text-color-secondary)', maxRotation: 45 }, grid: { color: 'var(--surface-border)' } },
        y: {
            ticks: { color: 'var(--text-color-secondary)', callback: (v) => formatCompact(v) },
            grid: { color: 'var(--surface-border)' },
        },
    },
});

// Renders a GSTR-3B section (list of {name_of_supplies|details|period, amounts}).
const AmountsTable = ({ rows, labelKey = 'name_of_supplies', labelHeader = 'Particulars', emptyText = 'No rows' }) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return <p className="text-600 text-sm m-0">{emptyText}</p>;
    }

    // Not every GSTR-3B section carries the same columns — the interstate table
    // has no CGST/SGST, the exempt table uses different keys entirely. Derive
    // the money columns from the data rather than hardcoding them.
    const amountKeys = Array.from(
        rows.reduce((set, r) => {
            Object.keys(r.amounts || {}).forEach((k) => set.add(k));
            return set;
        }, new Set()),
    );

    const HEADERS = {
        total_taxable_value: 'Taxable Value',
        integrated_tax: 'IGST',
        central_tax: 'CGST',
        state_ut_tax: 'SGST/UTGST',
        cess: 'Cess',
        interstate_supplies: 'Inter-state',
        intrastate_supplies: 'Intra-state',
    };
    // Taxable value first, then the tax columns, then anything unmapped.
    const ORDER = ['total_taxable_value', 'integrated_tax', 'central_tax', 'state_ut_tax', 'cess', 'interstate_supplies', 'intrastate_supplies'];
    const ordered = [
        ...ORDER.filter((k) => amountKeys.includes(k)),
        ...amountKeys.filter((k) => !ORDER.includes(k)),
    ];

    return (
        <DataTable
            value={rows}
            size="small"
            stripedRows
            showGridlines
            scrollable
            responsiveLayout="scroll"
            className="p-datatable-gridlines"
        >
            <Column
                field={labelKey}
                header={labelHeader}
                body={(r) => <span className="text-900 text-sm">{r[labelKey] ?? r.details ?? r.period ?? '—'}</span>}
                frozen
                alignFrozen="left"
                style={{ minWidth: '260px' }}
            />
            {ordered.map((k) => (
                <Column
                    key={k}
                    header={HEADERS[k] || k.replace(/_/g, ' ')}
                    body={(r) => {
                        const v = r.amounts?.[k];
                        // null means "not applicable on this row" in GSTR-3B, which
                        // is different from a genuine zero — show it as a dash.
                        if (v === null || v === undefined) return <span className="text-500">—</span>;
                        return <span className="font-medium">{formatINR(v)}</span>;
                    }}
                    style={{ minWidth: '130px' }}
                />
            ))}
        </DataTable>
    );
};

/**
 * GLIB (Autosift) GST Return Analysis (GSTR-3B) — output component.
 *
 * Built against a real 12-month vendor payload. Each monthly return arrives as
 * `returns[].gstr` with the GSTR-3B tables nested inside, so this component
 * derives a monthly timeline (turnover, tax liability, ITC) from those tables
 * and also exposes each return's full breakdown.
 *
 * Deliberately NO raw-JSON download — the analysis is read in the UI. The Excel
 * workbook stays available because that is the artefact credit teams file.
 */
const GlibGstReturnOutputComponent = ({
    reportData,
    hideCredits,
    isSharedView,
}) => {
    const shouldHideCredits = hideCredits || isSharedView;
    const [downloading, setDownloading] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const json = useMemo(() => {
        const rj = reportData?.report_json;
        if (!rj) return {};
        if (typeof rj !== 'string') return rj;
        try { return JSON.parse(rj); } catch { return {}; }
    }, [reportData]);

    const meta = useMemo(() => {
        if (!reportData) return null;
        return {
            isComplete: reportData.is_complete ?? (json.stage === 'completed'),
            isPartial: reportData.is_partial ?? json.is_partial ?? false,
            analysed: reportData.returns_analysed ?? json.returns_analysed ?? null,
            failed: reportData.returns_failed ?? json.returns_failed ?? 0,
            vendorStatus: reportData.vendor_status ?? json.latest_work_order?.status ?? null,
            workOrderId: reportData.work_order_id ?? json.work_order_id ?? null,
            excelUrl: (reportData.download_urls ?? json.download_urls ?? {})?.excel ?? null,
            remaining: reportData.remaining_credits ?? undefined,
        };
    }, [reportData, json]);

    // The parsed returns. Supports the live /status shape, the stored row, and a
    // raw vendor work order pasted straight in.
    const returns = useMemo(() => {
        if (!reportData) return [];
        if (Array.isArray(reportData.gstrs)) {
            return reportData.gstrs.filter((r) => r?.gstr);
        }
        if (Array.isArray(json.returns)) return json.returns;
        if (Array.isArray(json.gstrs)) return json.gstrs.filter((r) => r?.gstr);
        return [];
    }, [reportData, json]);

    const fileStatuses = useMemo(
        () => (Array.isArray(json.file_statuses) ? json.file_statuses : []),
        [json],
    );

    // Flatten each return into one timeline row.
    const timeline = useMemo(() => {
        const rows = returns.map((entry) => {
            const g = entry.gstr || {};
            const m = g.metadata || {};

            const outward = (g.inward_and_outward_supplies || [])
                .find((r) => String(r.name_of_supplies || '').startsWith(OUTWARD_TAXABLE));
            // The vendor's OCR occasionally mis-labels the ITC block: on some
            // returns `net_itc_available` holds the "ITC reclaimed" row and the
            // real "C Net ITC available" row is absent. Match the label strictly
            // and fall back to the "(5) All other ITC" row rather than silently
            // reporting whatever happens to sit in position 0 — see itcMissing.
            const netItcRow = (g.eligible_itc?.net_itc_available || [])
                .find((r) => String(r.details || '').startsWith(NET_ITC));
            const allOtherItc = (g.eligible_itc?.itc_available || [])
                .find((r) => String(r.details || '').includes('All other ITC'));
            const netItc = netItcRow || allOtherItc || null;
            const liability = (g.breakup_of_tax_liability_declared || [])[0];
            const lateFee = (g.interest_and_late_fee || [])
                .find((r) => String(r.details || '').toLowerCase().includes('late fee'));
            const interestPaid = (g.interest_and_late_fee || [])
                .find((r) => String(r.details || '').toLowerCase().includes('interest paid'));

            return {
                key: entry.id || `${m.month}-${m.year}`,
                month: titleCase(m.month),
                monthRaw: String(m.month || '').toLowerCase(),
                year: m.year || '—',
                period: liability?.period || `${titleCase(m.month)} ${m.year || ''}`.trim(),
                arn: m.arn || '—',
                filedOn: m.date_of_arn || '—',
                taxableValue: parseFloat(outward?.amounts?.total_taxable_value) || 0,
                cgst: parseFloat(outward?.amounts?.central_tax) || 0,
                sgst: parseFloat(outward?.amounts?.state_ut_tax) || 0,
                igst: parseFloat(outward?.amounts?.integrated_tax) || 0,
                taxLiability: sumAmounts(liability?.amounts),
                netItc: sumAmounts(netItc?.amounts),
                // True when the provider did not return a usable ITC row for
                // this month — distinguishes "genuinely nil ITC" from
                // "extraction failed", which look identical as ₹0.
                itcMissing: !netItc,
                lateFee: sumAmounts(lateFee?.amounts),
                interest: sumAmounts(interestPaid?.amounts),
                gstr: g,
            };
        });

        // Financial-year order (Apr→Mar), grouping by year first.
        return rows.sort((a, b) => {
            if (a.year !== b.year) return String(a.year).localeCompare(String(b.year));
            return (MONTH_INDEX[a.monthRaw] ?? 99) - (MONTH_INDEX[b.monthRaw] ?? 99);
        });
    }, [returns]);

    // Taxpayer identity — taken from the first return; all months share it.
    const taxpayer = useMemo(() => {
        const m = returns[0]?.gstr?.metadata || {};
        return {
            gstin: (m.gstin || '').toUpperCase() || '—',
            tradeName: titleCase(m.trade_name),
            legalName: titleCase(m.legal_name),
            signatory: m.name_of_authorised_signatory || '—',
            designation: titleCase(m.designation_of_authorised_signatory),
            documentType: (returns[0]?.gstr?.document_type || '').toUpperCase() || '—',
        };
    }, [returns]);

    const totals = useMemo(() => {
        const t = timeline.reduce((a, r) => ({
            turnover: a.turnover + r.taxableValue,
            liability: a.liability + r.taxLiability,
            itc: a.itc + r.netItc,
            lateFee: a.lateFee + r.lateFee,
            interest: a.interest + r.interest,
        }), { turnover: 0, liability: 0, itc: 0, lateFee: 0, interest: 0 });

        const months = timeline.length;
        return {
            ...t,
            months,
            // Months where the provider returned no usable ITC row. The ITC total
            // understates reality by however many of these there are.
            itcMissingMonths: timeline.filter((r) => r.itcMissing).map((r) => r.period),
            avgMonthly: months ? t.turnover / months : 0,
            // Annualised from whatever months are present — labelled as such in
            // the UI so a 4-month upload is never mistaken for a full year.
            netTaxPaid: t.liability - t.itc,
        };
    }, [timeline]);

    const turnoverChart = useMemo(() => ({
        labels: timeline.map((r) => `${r.month.slice(0, 3)} ${String(r.year).slice(2, 7)}`),
        datasets: [{
            label: 'Taxable Turnover',
            data: timeline.map((r) => r.taxableValue),
            backgroundColor: 'var(--primary-color)',
            borderRadius: 4,
        }],
    }), [timeline]);

    const taxChart = useMemo(() => ({
        labels: timeline.map((r) => `${r.month.slice(0, 3)} ${String(r.year).slice(2, 7)}`),
        datasets: [
            {
                label: 'Tax Liability',
                data: timeline.map((r) => r.taxLiability),
                borderColor: 'var(--red-400)',
                backgroundColor: 'transparent',
                tension: 0.35,
            },
            {
                label: 'Net ITC',
                data: timeline.map((r) => r.netItc),
                borderColor: 'var(--green-500)',
                backgroundColor: 'transparent',
                tension: 0.35,
            },
        ],
    }), [timeline]);

    if (!reportData) return <NoRecordsCard subtitle="No GST return analysis available" />;

    const downloadExcel = () => {
        if (!meta?.excelUrl) return;
        setDownloading(true);
        window.open(meta.excelUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => setDownloading(false), 800);
    };

    const hasReturns = timeline.length > 0;

    return (
        <>
            {meta?.isPartial && (
                <Message
                    severity="warn"
                    className="w-full mb-3"
                    content={
                        <div className="text-sm">
                            <div className="font-medium mb-1">Partial analysis</div>
                            <div className="text-700">
                                Only {meta.analysed} return(s) were analysed
                                {meta.failed ? ` — ${meta.failed} failed to upload` : ''}.
                                Turnover below covers an incomplete period.
                            </div>
                        </div>
                    }
                />
            )}

            {/* ---------------------------------------------------- header */}
            <Card className="mb-3">
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                    <h2 className="text-lg font-semibold text-900 m-0">
                        <i className="pi pi-percentage mr-2 text-primary" aria-hidden="true" />
                        GST Return Analysis
                    </h2>
                    <div className="flex flex-wrap align-items-center gap-2">
                        {taxpayer.documentType !== '—' && (
                            <Tag severity="info" value={taxpayer.documentType} className="text-sm" />
                        )}
                        {meta?.vendorStatus && (
                            <Tag
                                severity={meta.isComplete ? 'success' : 'info'}
                                icon={meta.isComplete ? 'pi pi-check-circle' : 'pi pi-clock'}
                                value={meta.isComplete ? 'Completed' : String(meta.vendorStatus)}
                                className="text-sm"
                            />
                        )}
                        {!shouldHideCredits && meta?.remaining !== undefined && meta.remaining !== null && (
                            <Tag severity="success" icon="pi pi-wallet" value={`Remaining: ${meta.remaining}`} className="text-sm" />
                        )}
                    </div>
                </div>

                {/* Taxpayer identity */}
                {hasReturns && (
                    <div className="grid mb-2">
                        <InfoItem label="GSTIN" value={taxpayer.gstin} mono />
                        <InfoItem label="Trade Name" value={taxpayer.tradeName} />
                        <InfoItem label="Legal Name" value={taxpayer.legalName} />
                        <InfoItem
                            label="Authorised Signatory"
                            value={taxpayer.signatory !== '—' ? `${taxpayer.signatory} (${taxpayer.designation})` : '—'}
                        />
                    </div>
                )}

                <div className="grid">
                    <StatTile
                        icon="pi-file"
                        label="Returns Filed"
                        value={formatNum(totals.months || meta?.analysed)}
                        sub={meta?.failed ? `${meta.failed} failed` : 'GSTR-3B returns analysed'}
                    />
                    <StatTile
                        icon="pi-chart-line"
                        label="Total Turnover"
                        value={formatINR(totals.turnover)}
                        sub={totals.months ? `Across ${totals.months} month(s)` : 'Taxable outward supplies'}
                    />
                    <StatTile
                        icon="pi-calendar"
                        label="Avg Monthly Turnover"
                        value={formatINR(totals.avgMonthly)}
                        sub="Total taxable value / months"
                    />
                    <StatTile
                        icon="pi-wallet"
                        label="Net ITC Available"
                        value={formatINR(totals.itc)}
                        sub="Input tax credit claimed"
                    />
                </div>

                {hasReturns && (
                    <div className="flex justify-content-center mt-3">
                        <Button
                            label={expanded ? 'Hide detailed analysis' : 'View detailed analysis'}
                            icon={expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                            iconPos="right"
                            text
                            onClick={() => setExpanded((v) => !v)}
                            aria-expanded={expanded}
                            className="w-full md:w-auto"
                        />
                    </div>
                )}
            </Card>

            {!hasReturns ? (
                <Card>
                    <div className="flex flex-column align-items-center py-5">
                        <i className="pi pi-hourglass pi-3x text-400 mb-3" aria-hidden="true" />
                        <h3 className="text-lg font-semibold text-900 m-0 mb-2">Detailed analysis not stored</h3>
                        <p className="text-600 text-sm m-0 text-center" style={{ maxWidth: '30rem' }}>
                            No parsed returns are available for this report. Download the Excel
                            workbook for the full breakdown, or run the analysis again.
                        </p>
                    </div>
                </Card>
            ) : expanded ? (
                <Card>
                    <TabView renderActiveOnly scrollable>
                        {/* ---------------------------------------- OVERVIEW */}
                        <TabPanel header="Overview" leftIcon="pi pi-chart-bar mr-2">
                            <Panel header="Filing Period" className="mb-3">
                                <div className="grid">
                                    <InfoItem
                                        label="Period Covered"
                                        value={timeline.length
                                            ? `${timeline[0].month} ${timeline[0].year} to ${timeline[timeline.length - 1].month} ${timeline[timeline.length - 1].year}`
                                            : '—'}
                                    />
                                    <InfoItem label="Returns Analysed" value={formatNum(totals.months)} />
                                    <InfoItem label="Total Tax Liability" value={totals.liability} money />
                                    <InfoItem label="Net Tax After ITC" value={totals.netTaxPaid} money />
                                </div>
                            </Panel>

                            <Panel header="Monthly Turnover" className="mb-3">
                                <Chart type="bar" data={turnoverChart} options={chartOptions()} style={{ height: '320px' }} />
                            </Panel>

                            <Panel header="Tax Liability vs Input Tax Credit" className="mb-3">
                                <Chart type="line" data={taxChart} options={chartOptions()} style={{ height: '320px' }} />
                            </Panel>
                        </TabPanel>

                        {/* ------------------------------------ MONTHLY TABLE */}
                        <TabPanel header="Monthly Summary" leftIcon="pi pi-calendar mr-2">
                            <Panel header="Turnover & Tax by Month" className="mb-3">
                                <DataTable
                                    value={timeline}
                                    size="small"
                                    stripedRows
                                    showGridlines
                                    scrollable
                                    responsiveLayout="scroll"
                                    className="p-datatable-gridlines"
                                    paginator={timeline.length > 12}
                                    rows={12}
                                >
                                    <Column
                                        field="period"
                                        header="Period"
                                        frozen
                                        alignFrozen="left"
                                        sortable
                                        style={{ minWidth: '150px' }}
                                    />
                                    <Column header="Taxable Turnover" body={(r) => <span className="font-medium">{formatINR(r.taxableValue)}</span>} sortable field="taxableValue" style={{ minWidth: '150px' }} />
                                    <Column header="CGST" body={(r) => formatINR(r.cgst)} style={{ minWidth: '120px' }} />
                                    <Column header="SGST/UTGST" body={(r) => formatINR(r.sgst)} style={{ minWidth: '130px' }} />
                                    <Column header="IGST" body={(r) => formatINR(r.igst)} style={{ minWidth: '120px' }} />
                                    <Column header="Tax Liability" body={(r) => <span className="font-medium">{formatINR(r.taxLiability)}</span>} sortable field="taxLiability" style={{ minWidth: '140px' }} />
                                    <Column
                                        header="Net ITC"
                                        body={(r) => (r.itcMissing
                                            ? <Tag severity="warning" value="Not extracted" className="text-xs" />
                                            : formatINR(r.netItc))}
                                        sortable
                                        field="netItc"
                                        style={{ minWidth: '140px' }}
                                    />
                                </DataTable>
                            </Panel>
                        </TabPanel>

                        {/* ------------------------------------------ ITC TAB */}
                        <TabPanel header="Input Tax Credit" leftIcon="pi pi-wallet mr-2">
                            {totals.itcMissingMonths.length > 0 && (
                                <Message
                                    severity="warn"
                                    className="w-full mb-3"
                                    content={
                                        <div className="text-sm">
                                            <div className="font-medium mb-1">
                                                ITC not extracted for {totals.itcMissingMonths.length} return(s)
                                            </div>
                                            <div className="text-700">
                                                {totals.itcMissingMonths.join(', ')} — the provider did not return a
                                                readable “Net ITC available” row, so these months contribute ₹0 and the
                                                ITC total below is understated. Check the Excel workbook or the original
                                                returns for the actual figures.
                                            </div>
                                        </div>
                                    }
                                />
                            )}
                            <Panel header="ITC Summary" className="mb-3">
                                <div className="grid">
                                    <InfoItem label="Total Net ITC" value={totals.itc} money />
                                    <InfoItem label="Total Tax Liability" value={totals.liability} money />
                                    <InfoItem label="Net Tax After ITC" value={totals.netTaxPaid} money />
                                    <InfoItem
                                        label="ITC Utilisation"
                                        value={totals.liability > 0
                                            ? `${((totals.itc / totals.liability) * 100).toFixed(1)}%`
                                            : '—'}
                                    />
                                </div>
                            </Panel>

                            <Panel header="ITC by Month" className="mb-3">
                                <DataTable
                                    value={timeline}
                                    size="small"
                                    stripedRows
                                    showGridlines
                                    scrollable
                                    responsiveLayout="scroll"
                                    className="p-datatable-gridlines"
                                >
                                    <Column field="period" header="Period" frozen alignFrozen="left" style={{ minWidth: '150px' }} />
                                    <Column
                                        header="Net ITC"
                                        body={(r) => (r.itcMissing
                                            ? <Tag severity="warning" value="Not extracted" className="text-xs" />
                                            : <span className="font-medium">{formatINR(r.netItc)}</span>)}
                                        style={{ minWidth: '140px' }}
                                    />
                                    <Column header="Tax Liability" body={(r) => formatINR(r.taxLiability)} style={{ minWidth: '140px' }} />
                                    <Column
                                        header="Net Payable"
                                        body={(r) => {
                                            // With ITC unextracted this would overstate what is payable.
                                            if (r.itcMissing) return <span className="text-500">—</span>;
                                            const net = r.taxLiability - r.netItc;
                                            return (
                                                <span className={net > 0 ? 'text-900 font-medium' : 'text-green-600 font-medium'}>
                                                    {formatINR(net)}
                                                </span>
                                            );
                                        }}
                                        style={{ minWidth: '140px' }}
                                    />
                                </DataTable>
                            </Panel>
                        </TabPanel>

                        {/* ----------------------------------- COMPLIANCE TAB */}
                        <TabPanel header="Compliance" leftIcon="pi pi-verified mr-2">
                            <div className="grid mb-3">
                                <StatTile
                                    icon="pi-check-circle"
                                    label="Returns Filed"
                                    value={formatNum(totals.months)}
                                    sub="GSTR-3B acknowledgements found"
                                    severity="success"
                                />
                                <StatTile
                                    icon="pi-exclamation-triangle"
                                    label="Total Late Fees"
                                    value={formatINR(totals.lateFee)}
                                    sub="Declared across returns"
                                    severity={totals.lateFee > 0 ? 'danger' : 'success'}
                                />
                                <StatTile
                                    icon="pi-percentage"
                                    label="Interest Paid"
                                    value={formatINR(totals.interest)}
                                    sub="On delayed payment"
                                    severity={totals.interest > 0 ? 'danger' : 'success'}
                                />
                                <StatTile
                                    icon="pi-file-excel"
                                    label="Files Processed"
                                    value={formatNum(fileStatuses.length || totals.months)}
                                    sub="Documents parsed by the provider"
                                />
                            </div>

                            <Panel header="Filing Acknowledgements" className="mb-3">
                                <DataTable
                                    value={timeline}
                                    size="small"
                                    stripedRows
                                    showGridlines
                                    scrollable
                                    responsiveLayout="scroll"
                                    className="p-datatable-gridlines"
                                >
                                    <Column field="period" header="Period" frozen alignFrozen="left" style={{ minWidth: '150px' }} />
                                    <Column field="arn" header="ARN" body={(r) => <span className="font-mono text-sm">{r.arn}</span>} style={{ minWidth: '180px' }} />
                                    <Column field="filedOn" header="Filed On" style={{ minWidth: '130px' }} />
                                    <Column header="Late Fee" body={(r) => (r.lateFee > 0
                                        ? <Tag severity="danger" value={formatINR(r.lateFee)} className="text-xs" />
                                        : <Tag severity="success" value="Nil" className="text-xs" />)} style={{ minWidth: '120px' }} />
                                    <Column header="Interest" body={(r) => (r.interest > 0
                                        ? <Tag severity="warning" value={formatINR(r.interest)} className="text-xs" />
                                        : <Tag severity="success" value="Nil" className="text-xs" />)} style={{ minWidth: '120px' }} />
                                </DataTable>
                            </Panel>

                            {fileStatuses.length > 0 && (
                                <Panel header="Document Processing" className="mb-3" toggleable collapsed>
                                    <DataTable
                                        value={fileStatuses}
                                        size="small"
                                        stripedRows
                                        showGridlines
                                        scrollable
                                        responsiveLayout="scroll"
                                        className="p-datatable-gridlines"
                                    >
                                        <Column field="file_name" header="File" frozen alignFrozen="left" style={{ minWidth: '260px' }} />
                                        <Column
                                            header="Status"
                                            body={(r) => (
                                                <Tag
                                                    severity={r.status === 'processed' ? 'success' : 'warning'}
                                                    value={titleCase(r.status)}
                                                    className="text-xs"
                                                />
                                            )}
                                            style={{ minWidth: '120px' }}
                                        />
                                        <Column field="num_pages" header="Pages" style={{ minWidth: '90px' }} />
                                        <Column
                                            header="Valid"
                                            body={(r) => (
                                                <Tag
                                                    severity={r.is_file_valid ? 'success' : 'danger'}
                                                    value={r.is_file_valid ? 'Yes' : 'No'}
                                                    className="text-xs"
                                                />
                                            )}
                                            style={{ minWidth: '100px' }}
                                        />
                                        <Column
                                            header="Scanned"
                                            body={(r) => <span className="text-700">{r.is_scanned ? 'Yes' : 'No'}</span>}
                                            style={{ minWidth: '110px' }}
                                        />
                                    </DataTable>
                                </Panel>
                            )}
                        </TabPanel>

                        {/* ----------------------------------- PER-RETURN TAB */}
                        <TabPanel header="Return Details" leftIcon="pi pi-list mr-2">
                            <p className="text-600 text-sm mt-0 mb-3">
                                Full GSTR-3B breakdown for each month, as filed.
                            </p>
                            <Accordion>
                                {timeline.map((r) => (
                                    <AccordionTab
                                        key={r.key}
                                        header={
                                            <div className="flex flex-wrap justify-content-between align-items-center gap-2 w-full">
                                                <span className="font-medium text-sm text-900">{r.period}</span>
                                                <div className="flex flex-wrap align-items-center gap-2">
                                                    <span className="text-600 text-xs">Turnover</span>
                                                    <Tag severity="info" value={formatINR(r.taxableValue)} className="text-xs" />
                                                </div>
                                            </div>
                                        }
                                    >
                                        <div className="grid mb-3">
                                            <InfoItem label="ARN" value={r.arn} mono />
                                            <InfoItem label="Filed On" value={r.filedOn} />
                                            <InfoItem label="Tax Liability" value={r.taxLiability} money />
                                            <InfoItem label="Net ITC" value={r.netItc} money />
                                        </div>

                                        <Panel header="Outward & Inward Supplies" className="mb-2" toggleable>
                                            <AmountsTable rows={r.gstr.inward_and_outward_supplies} />
                                        </Panel>

                                        <Panel header="Eligible ITC — Available" className="mb-2" toggleable collapsed>
                                            <AmountsTable rows={r.gstr.eligible_itc?.itc_available} labelKey="details" />
                                        </Panel>

                                        <Panel header="Eligible ITC — Reversed" className="mb-2" toggleable collapsed>
                                            <AmountsTable rows={r.gstr.eligible_itc?.itc_reversed} labelKey="details" />
                                        </Panel>

                                        <Panel header="Ineligible ITC" className="mb-2" toggleable collapsed>
                                            <AmountsTable rows={r.gstr.eligible_itc?.ineligible_itc} labelKey="details" />
                                        </Panel>

                                        <Panel header="Net ITC Available" className="mb-2" toggleable collapsed>
                                            <AmountsTable rows={r.gstr.eligible_itc?.net_itc_available} labelKey="details" />
                                        </Panel>

                                        <Panel header="Tax Liability Declared" className="mb-2" toggleable collapsed>
                                            <AmountsTable rows={r.gstr.breakup_of_tax_liability_declared} labelKey="period" labelHeader="Period" />
                                        </Panel>

                                        <Panel header="Interest & Late Fee" className="mb-2" toggleable collapsed>
                                            <AmountsTable rows={r.gstr.interest_and_late_fee} labelKey="details" />
                                        </Panel>

                                        <Panel header="Inter-state Supplies" className="mb-2" toggleable collapsed>
                                            <AmountsTable rows={r.gstr.outer_and_interstate_supplies} />
                                        </Panel>

                                        <Panel header="Exempt / Nil-rated / Non-GST Inward" className="mb-2" toggleable collapsed>
                                            <AmountsTable rows={r.gstr.exempt_nilrated_nongst_inward_supplies} />
                                        </Panel>
                                    </AccordionTab>
                                ))}
                            </Accordion>
                        </TabPanel>
                    </TabView>

                    {meta?.workOrderId && (
                        <p className="text-500 text-xs mt-3 mb-0">
                            Provider reference: <span className="font-mono">{meta.workOrderId}</span>
                        </p>
                    )}
                </Card>
            ) : null}

            {/* Excel download — last thing on the page. */}
            {meta?.excelUrl && (
                <Card className="mt-3">
                    <div className="flex flex-column md:flex-row md:align-items-center gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="text-900 text-sm font-semibold mb-1">
                                <i className="pi pi-file-excel mr-2 text-green-600" aria-hidden="true" />
                                Download the full report
                            </div>
                            <div className="text-600 text-xs">
                                Complete GSTR-3B workbook covering every uploaded return.
                            </div>
                        </div>
                        <Button
                            label="Download Excel"
                            icon="pi pi-download"
                            severity="success"
                            loading={downloading}
                            onClick={downloadExcel}
                            className="w-full md:w-auto"
                        />
                    </div>
                </Card>
            )}
        </>
    );
};

export default GlibGstReturnOutputComponent;
